import { MoreHorizontal } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { QuestionSet } from '@/api/questionSets';
import { Button } from '@/components/ui/Button';
import { DropdownMenu, type MenuItem } from '@/components/ui/DropdownMenu';

import { RetireSetDialog } from './RetireSetDialog';
import { mayWriteQuestionSet, type Viewer } from './setAccess';
import { SetDialog } from './SetDialog';

interface Props {
  set: QuestionSet;
  /** Who is looking — the write gate is a fact about **this row**, not about the tab. */
  viewer: Viewer;
  /** Called once *Retire* or *Restore* has been accepted, with the row and its new state. */
  onStateChanged?: (set: QuestionSet, nowActive: boolean) => void;
}

/**
 * §6.9's set row menu — *Rename* and **one** of *Retire* / *Restore*.
 *
 * ⚠️ **The whole menu is absent for a viewer who may not write the row** (`mayWriteQuestionSet`), and
 * §5 decides that shape: **hidden, never disabled** — a disabled item still tells the viewer the
 * action exists and invites them to ask for it. It is the **trigger** rather than the items that goes,
 * because a trigger that opens nothing is a control with nothing to do (`QuestionMenu`'s rule, and
 * `VacancyMenu`'s before it).
 *
 * ⚠️ **On a migrated tenant this hides the menu on *every* row for everybody but an `Admin`**, and
 * that is correct rather than a bug to work around: the ADR 0018 migration created every one of those
 * sets and could invent no author, so `author_id` is `null` and *"nobody wrote it"* is an `Admin`'s
 * alone. Reading `null` as *"nobody owns it, so anybody may"* would put a rename control over a whole
 * tenant's sets in front of every colleague and answer them 403.
 *
 * ⚠️ **There is no *Delete* here and there cannot be one** (ADR 0005 as ADR 0018 extends it to
 * `QuestionSet`, **feedback protection #2**): interviews were built from this set and still name it. *Retire* is the
 * answer, and it leaves the row, its name and every frozen question exactly where they are.
 *
 * ⚠️ **Retire and Restore are one item, never both** — `QuestionMenu`'s and `VacancyMenu`'s shape, and
 * the same argument: offering *Retire* on a retired row is a control with nothing to do, and offering
 * both asks the reader to work out which of them is the current state, which the state column already
 * says in words. Neither is `destructive`.
 *
 * ⚠️ **The item opens a dialog and writes nothing on selection.** A menu item that fired the request
 * itself would have nowhere to report a 403, a throttle or a lost connection.
 */
export function SetMenu({ set, viewer, onStateChanged }: Props) {
  const { t } = useTranslation('questions');
  const [renaming, setRenaming] = useState(false);
  const [switchingState, setSwitchingState] = useState(false);

  if (!mayWriteQuestionSet(set, viewer)) return null;

  const items: MenuItem[] = [
    { label: t('sets.menu.rename'), onSelect: () => setRenaming(true) },
    {
      label: set.is_active ? t('sets.menu.retire') : t('sets.menu.restore'),
      onSelect: () => setSwitchingState(true),
    },
  ];

  return (
    <>
      <DropdownMenu
        trigger={
          <Button
            variant="ghost"
            size="sm"
            /**
             * §4 Icons: an icon is never the only label. The name carries the **set's own name**,
             * because this tab has one of these per row and *"Actions"* alone would be a screenful of
             * identical controls to a screen reader (§9).
             */
            aria-label={t('sets.menu.label', { name: set.name })}
          >
            <MoreHorizontal size={16} strokeWidth={1.5} aria-hidden />
          </Button>
        }
        items={items}
      />

      {/* Mounted only while open, so the form's default — and the confirmation's direction — are the
          set as it is now. */}
      {renaming && <SetDialog set={set} onClose={() => setRenaming(false)} />}
      {switchingState && (
        <RetireSetDialog
          set={set}
          onCompleted={onStateChanged}
          onClose={() => setSwitchingState(false)}
        />
      )}
    </>
  );
}
